import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { isDraftPage, saveListingDraft } from 'library/helpers/listingDraft';
import styled, { keyframes } from 'styled-components';
import { FiArrowLeft, FiArrowRight, FiAlertCircle, FiMessageCircle, FiPhone, FiX, FiHeadphones } from 'react-icons/fi';
import { HOME_PAGE } from 'settings/constant';
import { ob, ONBOARDING_SECTIONS, IMPORT_SECTIONS } from './theme';
import { getImportEditSection } from 'library/helpers/importEditFlow';
import {
  ActionBar,
  ActionBarInner,
  ActionRow,
  Body,
  Content,
  GhostButton,
  HeaderBar,
  HeaderInner,
  HelpButton,
  HelpLabel,
  LiveDot,
  LogoButton,
  Main,
  Page,
  PageIntro,
  PageTitle,
  PendingBox,
  PrimaryButton,
  ProgressRail,
  Spinner,
} from './ui';

const LOGO_URL =
  'https://bid-your-stay.s3.ap-south-1.amazonaws.com/assets/images/logo/unpaqd_logo.svg';
const SUPPORT_PHONE = '917019620792';

/* ---------------- Help / contact sheet ---------------- */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SheetRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  @media (min-width: 640px) {
    align-items: center;
  }
`;

const SheetBackdrop = styled.button`
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(26, 26, 26, 0.3);
  backdrop-filter: blur(3px);
  cursor: default;
`;

const SheetCard = styled.div`
  position: relative;
  margin: 0 12px 12px;
  width: 100%;
  max-width: 448px;
  padding: 24px;
  border: 1px solid ${ob.border};
  border-radius: 20px;
  background: #fff;
  box-shadow: ${ob.shadowStrong};
  animation: ${slideUp} 250ms ease-out;
  @media (min-width: 640px) {
    margin-bottom: 0;
  }
  h2 {
    margin: 16px 0 0;
    font-family: ${ob.fontDisplay};
    font-size: 20px;
    font-weight: 600;
  }
  > p {
    margin: 4px 0 0;
    font-size: 14px;
    color: ${ob.muted};
  }
`;

const SheetClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: ${ob.mutedBg};
  color: ${ob.muted};
  cursor: pointer;
  &:hover {
    color: ${ob.text};
  }
`;

const SheetIcon = styled.span<{ $bg?: string; $fg?: string; $size?: number }>`
  display: grid;
  place-items: center;
  width: ${({ $size }) => $size ?? 44}px;
  height: ${({ $size }) => $size ?? 44}px;
  flex-shrink: 0;
  border-radius: 12px;
  background: ${({ $bg }) => $bg ?? ob.primarySoft};
  color: ${({ $fg }) => $fg ?? ob.primary};
  font-size: 20px;
`;

const ContactList = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${ob.border};
  border-radius: 12px;
  background: rgba(253, 252, 251, 0.8);
  color: ${ob.text};
  text-decoration: none;
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.4);
    background: rgba(255, 240, 230, 0.5);
  }
  .text {
    flex: 1;
  }
  .t {
    display: block;
    font-size: 14px;
    font-weight: 600;
  }
  .s {
    display: block;
    font-size: 12px;
    color: ${ob.muted};
  }
`;

const ContactSheet: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <SheetRoot role="dialog" aria-modal="true" aria-label="Talk to us">
      <SheetBackdrop type="button" aria-label="Close" onClick={onClose} />
      <SheetCard>
        <SheetClose type="button" aria-label="Close" onClick={onClose}>
          <FiX size={16} />
        </SheetClose>
        <SheetIcon>
          <FiMessageCircle />
        </SheetIcon>
        <h2>Talk to us</h2>
        <p>Real people, quick answers — pick whichever is easiest.</p>
        <ContactList>
          <ContactLink href={`https://wa.me/${SUPPORT_PHONE}`} target="_blank" rel="noreferrer">
            <SheetIcon $bg={ob.successSoft} $fg={ob.success} $size={40}>
              <FiMessageCircle />
            </SheetIcon>
            <span className="text">
              <span className="t">Chat on WhatsApp</span>
              <span className="s">Usually replies in a few minutes</span>
            </span>
            <FiArrowRight size={16} color={ob.muted} />
          </ContactLink>
          <ContactLink href={`tel:+${SUPPORT_PHONE}`}>
            <SheetIcon $bg={ob.warningSoft} $fg={ob.warning} $size={40}>
              <FiPhone />
            </SheetIcon>
            <span className="text">
              <span className="t">Call the host team</span>
              <span className="s">+91 70196 20792</span>
            </span>
            <FiArrowRight size={16} color={ob.muted} />
          </ContactLink>
        </ContactList>
      </SheetCard>
    </SheetRoot>
  );
};

/* ---------------- Shell ---------------- */

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderBackButton = styled.button`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  margin-left: -8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${ob.text};
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    background: rgba(26, 26, 26, 0.05);
    transform: translateX(-2px);
  }
`;

const ContextLabel = styled.span`
  display: none;
  padding-left: 12px;
  border-left: 1px solid ${ob.border};
  font-size: 14px;
  font-weight: 500;
  color: ${ob.muted};
  @media (min-width: 640px) {
    display: inline;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ImportLabel = styled.span`
  white-space: nowrap;
`;

export interface OnboardingShellProps {
  /** Index into ONBOARDING_SECTIONS for the first highlighted rail section. */
  section: number;
  /** Last highlighted rail section when one page covers several (e.g. Booking + House rules). */
  sectionThrough?: number;
  title?: string;
  intro?: string;
  wide?: boolean;
  /** Overrides the content width, e.g. "46rem". */
  maxWidth?: string;
  onBack?: () => void;
  onContinue?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  continueLabel?: string;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  /** Short messages shown above the bar telling the host what is still needed. */
  pending?: string[];
  /** Renders without the sticky bottom bar (screens that own their own actions). */
  hideActions?: boolean;
  /** Show the "Import it instead" button in the header (default: on for sections before Review). */
  showImport?: boolean;
  /** Shows a back arrow in the header (instead of the logo) — for pages with their own bottom buttons. */
  headerBack?: () => void;
  /** Replaces the progress rail labels (e.g. for the import flow); `section` then indexes into these. */
  railItems?: readonly string[];
  /** Replaces the header "Import it instead" button; only `action` is shown as its label. */
  strip?: { lead: string; action: string; onClick: () => void };
  /** The confirm-before-leaving modal from useOnboardingBackConfirm. */
  modal?: React.ReactNode;
  children: React.ReactNode;
}

const OnboardingShell: React.FC<OnboardingShellProps> = ({
  section,
  sectionThrough,
  title,
  intro,
  wide,
  maxWidth,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled,
  continueLoading,
  pending = [],
  hideActions,
  showImport,
  headerBack,
  railItems,
  strip,
  modal,
  children,
}) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // An edit started from a "Let us list it for you" page keeps that flow's tabs on the edit pages.
  const importEditSection = getImportEditSection();
  const [helpOpen, setHelpOpen] = useState(false);
  // What is still missing is only listed once the host has pressed Continue.
  const [showPending, setShowPending] = useState(false);
  useEffect(() => {
    if (pending.length === 0) setShowPending(false);
  }, [pending.length]);
  const pendingVisible = showPending && pending.length > 0 && !hideActions;

  // A brand-new listing keeps what the host has filled in as a draft (dashboard "Draft" tab), so
  // leaving part-way doesn't lose it. Saved shortly after each page opens, and again on leaving.
  useEffect(() => {
    if (!isDraftPage(pathname, search)) return undefined;
    const timer = window.setTimeout(() => {
      saveListingDraft(pathname);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [pathname, search]);

  const goHome = async (): Promise<void> => {
    if (isDraftPage(pathname, search)) {
      // Don't hold the host up if the save is slow.
      await Promise.race([saveListingDraft(pathname), new Promise((resolve) => setTimeout(resolve, 1500))]);
    }
    navigate(HOME_PAGE);
  };

  const showImportLink = showImport ?? section < ONBOARDING_SECTIONS.length - 1;
  const headerStrip = hideActions
    ? null
    : strip
    ? { label: strip.action.charAt(0).toUpperCase() + strip.action.slice(1), onClick: strip.onClick }
    : showImportLink
    ? { label: 'Import it instead', onClick: () => navigate('/host/import-listing') }
    : null;

  return (
    <Page>
      {modal}
      <HeaderBar>
        <HeaderInner>
          <LeftGroup>
            {headerBack ? (
              <HeaderBackButton type="button" aria-label="Go back" onClick={headerBack}>
                <FiArrowLeft size={20} />
              </HeaderBackButton>
            ) : (
              <LogoButton type="button" aria-label="Back to Unpaqd home" onClick={goHome}>
                <img src={LOGO_URL} alt="Unpaqd" />
              </LogoButton>
            )}
            <ContextLabel>Listing setup</ContextLabel>
          </LeftGroup>
          <HeaderActions>
            {headerStrip ? (
              <HelpButton
                type="button"
                onClick={() =>
                  Modal.confirm({
                    title: strip
                      ? 'Want to list it yourself instead?'
                      : 'Want to import your listing instead?',
                    content:
                      'Your saved progress on this listing will be lost if you switch. Do you really want to continue?',
                    okText: 'Yes, switch',
                    cancelText: 'Stay here',
                    onOk: headerStrip.onClick,
                  })
                }
                aria-label={headerStrip.label}
              >
                <ImportLabel>{headerStrip.label}</ImportLabel>
                <FiArrowRight size={16} />
              </HelpButton>
            ) : null}
            <HelpButton type="button" onClick={() => setHelpOpen(true)} aria-label="Need help? Chat with host support">
              <FiHeadphones size={16} />
              <HelpLabel />
              <LiveDot />
            </HelpButton>
          </HeaderActions>
        </HeaderInner>
      </HeaderBar>
      <ProgressRail
        items={railItems ?? (importEditSection !== null ? IMPORT_SECTIONS : ONBOARDING_SECTIONS)}
        active={railItems || importEditSection === null ? section : importEditSection}
        through={railItems || importEditSection === null ? sectionThrough : undefined}
      />
      <Main>
        <Content $wide={wide} $maxWidth={maxWidth}>
          {title ? <PageTitle>{title}</PageTitle> : null}
          {intro ? <PageIntro>{intro}</PageIntro> : null}
          <Body $pending={pendingVisible}>{children}</Body>
        </Content>
      </Main>
      {hideActions ? null : (
        <ActionBar>
          <ActionBarInner>
            {pendingVisible ? (
              <PendingBox role="status" aria-live="polite">
                <p>Still needed to continue:</p>
                <ul>
                  {pending.map((item) => (
                    <li key={item}>
                      <FiAlertCircle size={12} />
                      {item}
                    </li>
                  ))}
                </ul>
              </PendingBox>
            ) : null}
            <ActionRow>
              {onBack ? (
                <GhostButton type="button" onClick={onBack}>
                  <FiArrowLeft size={16} /> Back
                </GhostButton>
              ) : (
                <span />
              )}
              {onContinue ? (
                <PrimaryButton
                  type="button"
                  onClick={(e) => {
                    if (pending.length > 0) setShowPending(true);
                    // A page that is blocked by missing items stays clickable, so pressing
                    // Continue can show what is missing instead of doing nothing.
                    if (continueDisabled) return;
                    onContinue(e);
                  }}
                  disabled={(continueDisabled && pending.length === 0) || continueLoading}
                >
                  {continueLoading ? <Spinner /> : null}
                  {continueLabel} <FiArrowRight size={16} />
                </PrimaryButton>
              ) : null}
            </ActionRow>
          </ActionBarInner>
        </ActionBar>
      )}
      <ContactSheet open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Page>
  );
};

export default OnboardingShell;
